import type { Outputs } from "@postdaily/api/routers/index";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { buttonVariants } from "@postdaily/ui/components/button";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";

import { AnalyticsKpiCards } from "@/components/analytics/analytics-kpi-cards";
import { AudienceGrowthCard } from "@/components/analytics/audience-growth-card";
import { BestTimesCard } from "@/components/analytics/best-times-card";
import { CapabilityBanner } from "@/components/analytics/capability-note";
import { DateRangeControl } from "@/components/analytics/date-range-control";
import { EngagementOverTimeChart } from "@/components/analytics/engagement-over-time-chart";
import { ExportButton } from "@/components/analytics/export-button";
import { TopPostsCard } from "@/components/analytics/top-posts-card";
import { useAnalyticsEntitlements } from "@/components/analytics/use-analytics-entitlements";
import {
  type AnalyticsRangeState,
  useAnalyticsRange,
} from "@/components/analytics/use-analytics-range";
import { ChannelAvatar } from "@/components/channels/channel-avatar";
import { PageHeader } from "@/components/page-header";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { getChannelLabel } from "@/lib/channel-label";
import { getProviderLabel } from "@/lib/provider-labels";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/channels/$id/analytics")({
  component: RouteComponent,
});

type SocialAccount = Outputs["socialAccounts"]["list"][number];

function RouteComponent() {
  const { id } = Route.useParams();
  const { currentWorkspace } = useCurrentWorkspace();
  const trpc = useTRPC();
  const accountsQuery = useQuery({
    ...trpc.socialAccounts.list.queryOptions({
      workspaceId: currentWorkspace?.id ?? "",
    }),
    enabled: Boolean(currentWorkspace?.id),
  });

  if (!currentWorkspace || accountsQuery.isPending) {
    return <Skeleton className="h-72 w-full" />;
  }

  if (accountsQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Channel unavailable</AlertTitle>
        <AlertDescription>{accountsQuery.error.message}</AlertDescription>
      </Alert>
    );
  }

  const account = accountsQuery.data.find((item) => item.id === id);

  if (!account) {
    return (
      <div className="flex flex-col gap-4">
        <Alert variant="destructive">
          <AlertTitle>Channel not found</AlertTitle>
          <AlertDescription>
            This channel isn’t in the current workspace.
          </AlertDescription>
        </Alert>
        <Link
          to="/channels"
          className={buttonVariants({ variant: "outline", size: "sm" })}
        >
          <ArrowLeftIcon data-icon="inline-start" />
          All channels
        </Link>
      </div>
    );
  }

  return (
    <ChannelAnalytics account={account} workspaceId={currentWorkspace.id} />
  );
}

function ChannelAnalytics({
  account,
  workspaceId,
}: {
  account: SocialAccount;
  workspaceId: string;
}) {
  const analyticsEntitlements = useAnalyticsEntitlements(workspaceId);
  const range = useAnalyticsRange(analyticsEntitlements.retentionDays);
  const name = getChannelLabel(account);
  const handle = account.providerAccountHandle;

  return (
    <div className="mx-auto flex w-full max-w-[1240px] flex-col gap-5">
      <PageHeader
        leading={
          <ChannelAvatar
            layout="overlay"
            name={name}
            avatarUrl={account.avatarUrl}
            provider={account.provider}
            size="lg"
          />
        }
        title={name}
        description={`${getProviderLabel(account.provider)}${handle ? ` · ${handle}` : ""}`}
        actions={
          <>
            <DateRangeControl
              range={range}
              retentionDays={analyticsEntitlements.retentionDays}
            />
            <ExportButton
              canExport={analyticsEntitlements.canExportCsv}
              datasets={["posts", "daily"]}
              from={range.from}
              socialAccountId={account.id}
              to={range.to}
              workspaceId={workspaceId}
            />
          </>
        }
      />

      <ChannelAnalyticsBody
        account={account}
        range={range}
        workspaceId={workspaceId}
      />
    </div>
  );
}

function ChannelAnalyticsBody({
  account,
  range,
  workspaceId,
}: {
  account: SocialAccount;
  range: AnalyticsRangeState;
  workspaceId: string;
}) {
  const trpc = useTRPC();
  const channel = useQuery(
    trpc.analytics.channel.queryOptions({
      from: range.from,
      socialAccountId: account.id,
      to: range.to,
      workspaceId,
    }),
  );

  if (channel.isPending) {
    return (
      <div className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
        <Skeleton className="h-80 w-full rounded-2xl" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-72 w-full rounded-2xl" />
          <Skeleton className="h-72 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (channel.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Channel analytics unavailable</AlertTitle>
        <AlertDescription>{channel.error.message}</AlertDescription>
      </Alert>
    );
  }

  const data = channel.data;

  return (
    <>
      <CapabilityBanner note={data.support.note} />
      <AnalyticsKpiCards kpis={data.kpis} syncedAt={data.syncedAt} />
      <EngagementOverTimeChart scope="channel" series={data.series} />
      <div className="grid gap-4 lg:grid-cols-2">
        <BestTimesCard
          provider={data.account.provider}
          socialAccountId={account.id}
          workspaceId={workspaceId}
        />
        <AudienceGrowthCard
          audience={{
            accounts: [],
            growth: data.followers.growth,
            // The per-channel page has a follower reading exactly when its
            // series has a point; without this a channel sitting at 0 followers
            // renders as "—", i.e. indistinguishable from one we never read.
            hasSnapshots: data.followers.series.length > 0,
            series: data.followers.series,
            total: data.followers.total,
          }}
        />
      </div>
      <TopPostsCard items={data.topPosts} />
      <div>
        <Link
          className={buttonVariants({ variant: "outline", size: "sm" })}
          search={{ account: account.id, tab: "posts" }}
          to="/analytics"
        >
          All posts from this channel
        </Link>
      </div>
    </>
  );
}
